import { useState, useEffect } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiError } from "../../lib/api";
import { bedrijf } from "../../data/content";

const soorten = [
  "Basisfeest (verjaardag/jubileum)",
  "Themafeest",
  "Buffetarrangement",
  "Bedrijfsfeest / borrel",
  "Familiefeest",
  "Vergaderen met eten",
  "Anders",
];

const leeg = { naam: "", email: "", telefoon: "", soort_bijeenkomst: soorten[0], datum: "", aantal_personen: "", bericht: "" };

export const Contact = () => {
  const [form, setForm] = useState(leeg);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const { soort, bericht } = e.detail || {};
      setForm((f) => ({
        ...f,
        soort_bijeenkomst: soort && soorten.includes(soort) ? soort : f.soort_bijeenkomst,
        bericht: bericht ? `${bericht}\n\n` : f.bericht,
      }));
    };
    window.addEventListener("prefill-aanvraag", handler);
    return () => window.removeEventListener("prefill-aanvraag", handler);
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await client.post("/contact", form);
      toast.success(data.message || "Bedankt voor je aanvraag!");
      setForm(leeg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Versturen mislukt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
        <div>
          <span className="text-primary font-semibold text-sm uppercase tracking-wide">Contact</span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mt-3">
            Iets te vieren of te organiseren?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            Laat hieronder je gegevens achter, dan nemen we snel contact met je op. Liever even bellen? Dat mag natuurlijk ook.
          </p>

          <div className="mt-8 space-y-4">
            <a data-testid="contact-phone" href={`tel:${bedrijf.telefoonRaw}`} className="flex items-center gap-4 bg-card border border-border rounded-2xl p-5 hover:border-primary/40 transition-colors">
              <span className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center"><Phone size={20} className="text-primary" /></span>
              <span><span className="block text-sm text-muted-foreground">Bel ons</span><span className="font-medium">{bedrijf.telefoon}</span></span>
            </a>
            <a href={`mailto:${bedrijf.email}`} className="flex items-center gap-4 bg-card border border-border rounded-2xl p-5 hover:border-primary/40 transition-colors">
              <span className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center"><Mail size={20} className="text-primary" /></span>
              <span><span className="block text-sm text-muted-foreground">Mail ons</span><span className="font-medium break-all">{bedrijf.email}</span></span>
            </a>
            <a href={bedrijf.mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-card border border-border rounded-2xl p-5 hover:border-primary/40 transition-colors">
              <span className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center"><MapPin size={20} className="text-primary" /></span>
              <span><span className="block text-sm text-muted-foreground">Bezoek ons</span><span className="font-medium">{bedrijf.adres}, {bedrijf.postcode}</span></span>
            </a>
          </div>
        </div>

        <form data-testid="contact-form" onSubmit={submit} className="bg-card border border-border rounded-2xl p-6 md:p-8 space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1.5">Naam</label>
            <input data-testid="contact-naam" required value={form.naam} onChange={update("naam")} className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all" placeholder="Je naam" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium mb-1.5">E-mailadres</label>
              <input data-testid="contact-email" type="email" required value={form.email} onChange={update("email")} className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all" placeholder="naam@email.nl" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Telefoonnummer</label>
              <input data-testid="contact-telefoon" required value={form.telefoon} onChange={update("telefoon")} className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all" placeholder="06 - ..." />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Soort bijeenkomst</label>
            <select data-testid="contact-soort" value={form.soort_bijeenkomst} onChange={update("soort_bijeenkomst")} className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all">
              {soorten.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium mb-1.5">Gewenste datum <span className="text-muted-foreground font-normal">(optioneel)</span></label>
              <input data-testid="contact-datum" type="date" value={form.datum} onChange={update("datum")} className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Aantal personen <span className="text-muted-foreground font-normal">(optioneel)</span></label>
              <input data-testid="contact-aantal" type="number" min="1" value={form.aantal_personen} onChange={update("aantal_personen")} className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all" placeholder="bijv. 50" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Bericht</label>
            <textarea data-testid="contact-bericht" required rows={4} value={form.bericht} onChange={update("bericht")} className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all resize-none" placeholder="Vertel kort waar we je mee kunnen helpen, met hoeveel personen en wanneer." />
          </div>
          <button data-testid="contact-submit" type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground font-medium rounded-full px-6 py-3.5 hover:bg-primary-hover transition-colors disabled:opacity-60">
            {loading ? "Versturen..." : "Verstuur aanvraag"}
          </button>
          <p className="text-sm text-muted-foreground text-center">We reageren meestal binnen één werkdag.</p>
        </form>
      </div>
    </section>
  );
};
